/**
 * Super-admin editor for the SCR import column mapping.
 *
 * Each row maps a `source` column header from the SCR export (e.g.
 * `price/formattedValue`) to a `target` Property field (e.g. `prezzo`) with
 * an optional value transform (money/number/bool/…). Rows can be added,
 * removed, or reset to the customer-provided defaults.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const AdminScrMapping = () => {
  const [entries, setEntries] = useState([]);
  const [targets, setTargets] = useState({});
  const [transforms, setTransforms] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/scr-mapping");
      setEntries(r.data.entries || []);
      setTargets(r.data.targets || {});
      setTransforms(r.data.transforms || []);
    } catch (e) {
      toast.error("Errore caricamento mapping");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (i, patch) => setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const del = (i) => setEntries((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => setEntries((prev) => [...prev, { source: "", target: "citta", transform: "text" }]);

  const save = async () => {
    // Client-side validation — server also validates.
    for (const e of entries) {
      if (!e.source || !e.source.trim()) { toast.error("Ogni riga deve avere una colonna sorgente"); return; }
      if (!targets[e.target]) { toast.error(`Campo destinazione non valido: ${e.target}`); return; }
      if (!transforms.includes(e.transform)) { toast.error(`Trasformazione non valida: ${e.transform}`); return; }
    }
    setSaving(true);
    try {
      await api.put("/admin/scr-mapping", { entries });
      toast.success(`Mapping salvato (${entries.length} righe)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Ripristinare il mapping predefinito? Le personalizzazioni verranno perse.")) return;
    try {
      await api.post("/admin/scr-mapping/reset");
      await load();
      toast.success("Mapping ripristinato");
    } catch { toast.error("Errore ripristino"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-scr-mapping-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Import SCR</div>
          <h2 className="font-display font-bold text-xl tracking-tight mt-1">Mapping colonne</h2>
          <p className="text-xs text-slate-500 mt-1">
            Definisci come le colonne dell'export SCR vengono mappate ai campi degli immobili.
            Ogni riga = <b>colonna sorgente</b> (intestazione nel CSV) → <b>campo destinazione</b> → eventuale <b>trasformazione</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full" onClick={resetDefaults} data-testid="admin-scr-reset">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Ripristina default
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#0A84FF]" data-testid="admin-scr-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvataggio…" : "Salva mapping"}
          </Button>
        </div>
      </div>

      <div className="ios-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold w-[45%]">Colonna sorgente (CSV)</th>
              <th className="text-left px-4 py-2.5 font-semibold w-[25%]">Campo destinazione</th>
              <th className="text-left px-4 py-2.5 font-semibold w-[20%]">Trasformazione</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Nessuna riga. Clicca "Aggiungi riga" per iniziare.</td></tr>
            )}
            {entries.map((e, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`admin-scr-row-${i}`}>
                <td className="px-4 py-2">
                  <Input
                    value={e.source}
                    onChange={(ev) => upd(i, { source: ev.target.value })}
                    placeholder="properties/0/location/city"
                    className="font-mono text-xs"
                    data-testid={`admin-scr-row-${i}-source`}
                  />
                </td>
                <td className="px-4 py-2">
                  <select
                    value={e.target}
                    onChange={(ev) => upd(i, { target: ev.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 h-9 text-sm bg-white"
                    data-testid={`admin-scr-row-${i}-target`}
                  >
                    {Object.entries(targets).map(([k, label]) => (
                      <option key={k} value={k}>{label} ({k})</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <select
                    value={e.transform}
                    onChange={(ev) => upd(i, { transform: ev.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 h-9 text-sm bg-white"
                    data-testid={`admin-scr-row-${i}-transform`}
                  >
                    {transforms.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => del(i)} className="text-rose-600 hover:text-rose-900" data-testid={`admin-scr-row-${i}-delete`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 border-t border-slate-100 bg-slate-50 flex justify-center">
          <button
            onClick={add}
            className="inline-flex items-center gap-1.5 text-sm text-[#0A84FF] hover:underline"
            data-testid="admin-scr-add-row"
          >
            <Plus className="w-4 h-4" /> Aggiungi riga
          </button>
        </div>
      </div>

      <div className="ios-card p-4 bg-slate-50/60">
        <div className="text-[11px] text-slate-500 leading-relaxed">
          <div className="font-semibold text-slate-700 mb-1">Trasformazioni disponibili</div>
          <ul className="space-y-0.5">
            <li><code className="text-slate-800">text</code> — passa il valore così com'è</li>
            <li><code className="text-slate-800">number</code> — converte in numero (accetta virgole/decimali)</li>
            <li><code className="text-slate-800">money</code> — stessa cosa ma ripulisce "€", separatori migliaia</li>
            <li><code className="text-slate-800">bool</code> — true / false</li>
            <li><code className="text-slate-800">ristrutturato</code> — Ottimo/ristrutturato → <b>SI</b>, Buono/abitabile → <b>NI</b>, Da ristrutturare → <b>NO</b></li>
            <li><code className="text-slate-800">seller_type</code> — determina Agenzia / Privato dal valore del campo</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminScrMapping;
